// src/pages/UserManagementPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Paper,
  TextField,
  Button,
  Alert,
  Chip,
} from "@mui/material";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../../components/ui/Pagination";
import SortableTable, { SortableColumn } from "../../components/common/SortableTable";
import CustomLoading from "../../components/common/CustomLoading";
import ActionIcon from "../../../src/assets/icons/ActionIcon.svg";
import apiService, { parseGetAllUsersResponse } from "../../services/api";
import { DATE_FORMATS } from "../../constants";
import SearchableSelectInput from "../../components/forms/SearchableSelectInput";
import useCodeCommonByCategory from "../../hooks/useCodeCommonByCategory";
import { formatMobileNumber } from "../../utils/formatters";

// ---------- Helper Components ----------
const StatusChip = ({ status }: { status: string }) => {
  if (!status) return null;

  const STATUS_STYLES: Record<string, { bg: string; color: string }> = {
    Active: { bg: "rgba(212, 255, 199, 1)", color: "rgba(31, 113, 13, 1)" },
    Banned: { bg: "rgba(255, 227, 227, 1)", color: "rgba(197, 0, 0, 1)" },
    Absconded: { bg: "rgba(255, 235, 205, 1)", color: "#FF3B30" },
    TemporarilyDeleted: { bg: "rgba(255, 244, 229, 1)", color: "rgba(178, 106, 0, 1)" },
    PermanentlyDeleted: { bg: "rgba(238, 238, 238, 1)", color: "rgba(97, 97, 97, 1)" },
  };

  return (
    <Chip
      label={status}
      size="medium"
      sx={{
        backgroundColor: STATUS_STYLES[status]?.bg || "rgba(245, 245, 245, 1)",
        color: STATUS_STYLES[status]?.color || "rgba(117, 117, 117, 1)",
        fontWeight: 600,
      }}
    />
  );
};

const KycChip = ({ kyc }: { kyc: string }) => {
  if (!kyc) return null;

  const KYC_STYLES: Record<string, { bg: string; color: string }> = {
    Verified: {
      bg: "rgba(212, 255, 199, 1)",
      color: "rgba(31, 113, 13, 1)",
    },
    Pending: {
      bg: "rgba(255, 248, 220, 1)",
      color: "rgba(184, 134, 11, 1)",
    },
    Failed: {
      bg: "rgba(255, 227, 227, 1)",
      color: "rgba(197, 0, 0, 1)",
    },
    "Needs review": {
      bg: "rgba(227, 242, 253, 1)",
      color: "rgba(21, 101, 192, 1)",
    },
    Rejected: {
      bg: "rgba(255, 227, 227, 1)",
      color: "rgba(197, 0, 0, 1)",
    },
  };

  return (
    <Chip
      label={kyc}
      size="medium"
      sx={{
        backgroundColor: KYC_STYLES[kyc]?.bg || "rgba(245, 245, 245, 1)",
        color: KYC_STYLES[kyc]?.color || "rgba(117, 117, 117, 1)",
        fontWeight: 600,
      }}
    />
  );
};

// ---------- Types ----------
interface UserData {
  id: string;
  userName: string;
  mobileNo: string;
  email: string;
  status: string;
  kyc: string;
  totalTrips: number;
  joined: string;
}

interface Filters {
  search: string;
  status: string;
  kyc: string;
}

// ---------- Main Component ----------
const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const getInitialFilters = () => {
    const state = location.state as any;
    return {
      search: state?.search || "",
      status: state?.status || "",
      kyc: state?.kyc || "",
    };
  };
  const getInitialPage = () => (location.state as any)?.page || 0;

  const [filters, setFilters] = useState<Filters>(getInitialFilters());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rawUsersData, setRawUsersData] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);

  const { statusOptions, getDisplayNameByCodeValue, getCodeValueByDisplayName } = useCodeCommonByCategory('UserStatus');

  useEffect(() => {
    const state = location.state as any;
    if (state) {
      setFilters({
        search: state.search ?? '',
        status: state.status ?? '',
        kyc: state.kyc ?? '',
      });
    }
  }, [location.state]);

  const pagination = usePagination({
    totalItems: totalCount,
    initialPage: getInitialPage(),
    initialRowsPerPage: 10,
  });

  const transformUserData = useCallback((user: any): UserData => {
    const getKycText = (kyc: any) => {
      // kycstatus: 0 = failed, 1 = verified, 2 = needs review; missing → Pending
      if (kyc === null || kyc === undefined || kyc === "") return "Pending";
      const n = typeof kyc === "number" ? kyc : Number.parseInt(String(kyc), 10);
      if (n === 0) return "Failed";
      if (n === 1) return "Verified";
      if (n === 2) return "Needs review";
      return "Pending";
    };

    // Get status display name from codevalue using the status codes
    const statusDisplayName = getDisplayNameByCodeValue(user?.userstatus) || "";

    return {
      id: user?.userid,
      userName: user?.name || "",
      mobileNo: formatMobileNumber(user?.phonenumber) || "",
      email: user?.emailid || "",
      status: statusDisplayName,
      kyc: getKycText(user?.kycstatus),
      totalTrips: user?.totalTrips || 0,
      joined: DATE_FORMATS.formatDateTime(user?.joineddate || ""),
    };
  }, [getDisplayNameByCodeValue]);

  const rows = useMemo(
    () => rawUsersData.map(transformUserData),
    [rawUsersData, transformUserData]
  );

  const handleFilterChange = (field: string, value: string) => {
    const newFilters = { ...filters, [field]: value };
    setFilters(newFilters);
    pagination.goToPage(0);
    navigate(location.pathname, {
      replace: true,
      state: {
        ...((location.state as object) || {}),
        ...newFilters,
        page: 0,
      },
    });
  };

  const fetchUserManagement = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const statusCodeValue = filters.status ? getCodeValueByDisplayName(filters.status) : null;
      const parsedStatus =
        statusCodeValue != null && statusCodeValue !== ""
          ? Number.parseInt(statusCodeValue, 10)
          : NaN;
      const userstatus = Number.isFinite(parsedStatus) ? parsedStatus : null;

      let kycstatus: number | null = null;
      if (filters.kyc === "Verified") kycstatus = 1;
      else if (filters.kyc === "Failed") kycstatus = 0;
      else if (filters.kyc === "Needs review") kycstatus = 2;

      const payload = {
        name: filters.search.trim() || null,
        roleid: null,
        hubid: null,
        userstatus,
        kycstatus,
        registrationdate: null,
        reporttype: "user",
        pageNumber: pagination.page + 1,
        pageSize: pagination.rowsPerPage,
      };

      const response = await apiService.getAllUsers(payload);
      const { items, total } = parseGetAllUsersResponse(response);
      setRawUsersData(Array.isArray(items) ? items : []);
      setTotalCount(total);
    } catch (err: any) {
      setError(err.message || "Failed to fetch users");
      setRawUsersData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [
    filters.search,
    filters.status,
    filters.kyc,
    pagination.page,
    pagination.rowsPerPage,
    getCodeValueByDisplayName,
  ]);

  useEffect(() => {
    fetchUserManagement();
  }, [fetchUserManagement]);

  // ---------- Table Columns ----------
  const columns: SortableColumn<UserData>[] = [
    { id: "userName", label: "User Name" },
    { id: "mobileNo", label: "Mobile No." },
    { id: "email", label: "Email" },
    {
      id: "status",
      label: "Status",
      align: "center",
      render: (row) => <StatusChip status={row.status} />,
    },
    {
      id: "kyc",
      label: "KYC",
      align: "center",
      render: (row) => <KycChip kyc={row.kyc} />,
    },
    { id: "joined", label: "Joined" },
    {
      id: "action" as keyof UserData,
      label: "Action",
      align: "center",
      sortable: false,
      render: (row) => (
        <Button
          variant="text"
          sx={{ p: 0, minWidth: "auto" }}
          onClick={() => {
            navigate(`/user-management/user-details?id=${row.id}`, {
              state: {
                search: filters.search,
                status: filters.status,
                kyc: filters.kyc,
                page: pagination.page
              },
            });
          }}
        >
          <img src={ActionIcon} alt="Action" width={30} height={30} />
        </Button>
      ),
    },
  ];

  // ---------- Render ----------
  return (
    <>
      <Container fluid className="px-0">
        {/* Error Alert */}
        {error && (
          <Alert severity="error" className="mb-3" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filters */}
        <Paper className="mb-3 p-3 border-0 rounded-0 shadow-none">
          <Row className="g-3 align-items-end">
            <Col xs={12} md={3}>
              <TextField
                label="User Name"
                placeholder="Enter user name"
                size="small"
                fullWidth
                value={filters.search}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  handleFilterChange("search", e.target.value)
                }
              />
            </Col>

            <Col xs={12} md={2}>
              <SearchableSelectInput
                field="status"
                label="Status"
                placeholder="Select status"
                formData={filters}
                onChange={handleFilterChange}
                options={statusOptions}
              />
            </Col>

            <Col xs={12} md={2}>
              <SearchableSelectInput
                field="kyc"
                label="KYC Status"
                placeholder="Select KYC status"
                formData={filters}
                onChange={handleFilterChange}
                options={["Verified", "Failed", "Needs review"]}
              />
            </Col>

          </Row>
        </Paper>

        {/* Table */}
        <Paper className="border-0 rounded-0 shadow-none">
          {loading ? (
            <CustomLoading message="Fetching Users Table Data..." />
          ) : (
            <SortableTable
              data={rows}
              columns={columns}
              emptyMessage="No Users Data found"
            />
          )}
        </Paper>
      </Container>

      {/* Pagination */}
      <Pagination
        page={pagination.page}
        rowsPerPage={pagination.rowsPerPage}
        totalItems={totalCount}
        totalPages={pagination.totalPages}
        startItem={pagination.startItem}
        endItem={pagination.endItem}
        rowsPerPageOptions={[5, 10, 25, 50]}
        showRowsPerPage
        onRowsPerPageChange={pagination.handleChangeRowsPerPage}
        onGoToNextPage={pagination.goToNextPage}
        onGoToPreviousPage={pagination.goToPreviousPage}
      />
    </>
  );
};

export default UserManagementPage;
